import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileSignature,
  Download,
  Search,
  RefreshCw,
  ShieldCheck,
  Mail,
  Calendar as CalendarIcon,
  Globe,
} from "lucide-react";
import { toast } from "sonner";

const DOCUMENT_LABELS = {
  figc_cartellino: "Cartellino FIGC",
  medical_request: "Richiesta visita medica",
  enrollment: "Iscrizione società",
  privacy_release: "Liberatoria privacy",
};

const DOCUMENT_COLORS = {
  figc_cartellino: "bg-sky-100 text-sky-700 border-sky-200",
  medical_request: "bg-emerald-100 text-emerald-700 border-emerald-200",
  enrollment: "bg-amber-100 text-amber-700 border-amber-200",
  privacy_release: "bg-violet-100 text-violet-700 border-violet-200",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function dateOnly(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n\r;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function SignaturesAuditPage() {
  const { activeTeam } = useTeams();
  const teamId = activeTeam?.id;
  const isParent = activeTeam?.my_role === "parent";

  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [docFilter, setDocFilter] = useState(searchParams.get("type") || "__all__");
  const [search, setSearch] = useState(searchParams.get("ref") || searchParams.get("q") || "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // keep URL in sync with filters
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set("q", search);
    if (docFilter !== "__all__") next.set("type", docFilter);
    setSearchParams(next, { replace: true });
  }, [search, docFilter, setSearchParams]);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/signatures`);
      setSignatures(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento firme");
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return signatures.filter((s) => {
      if (docFilter !== "__all__" && s.document_type !== docFilter) return false;
      if (q) {
        const hay = [
          s.signer_email,
          s.signer_name,
          s.signer_role,
          s.document_ref,
          s.ip_address,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (from && dateOnly(s.signed_at) < from) return false;
      if (to && dateOnly(s.signed_at) > to) return false;
      return true;
    });
  }, [signatures, docFilter, search, from, to]);

  const stats = useMemo(() => {
    const total = signatures.length;
    const byType = {};
    const last7d = new Date(Date.now() - 7 * 86400000).toISOString();
    let recent = 0;
    for (const s of signatures) {
      byType[s.document_type] = (byType[s.document_type] || 0) + 1;
      if (s.signed_at && s.signed_at >= last7d) recent += 1;
    }
    return { total, byType, recent };
  }, [signatures]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nessuna firma da esportare");
      return;
    }
    const headers = [
      "Data firma",
      "Documento",
      "Riferimento",
      "Firmatario",
      "Email",
      "Ruolo",
      "IP",
      "User Agent",
      "Metodo",
      "ID firma",
    ];
    const rows = filtered.map((s) => [
      s.signed_at || "",
      DOCUMENT_LABELS[s.document_type] || s.document_type,
      s.document_ref || "",
      s.signer_name || "",
      s.signer_email || "",
      s.signer_role || "",
      s.ip_address || "",
      s.user_agent || "",
      s.method || "",
      s.id || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `firme-audit-${activeTeam?.name || "team"}-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportate ${filtered.length} firme`);
  };

  if (!teamId) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-400">
        Seleziona una squadra per visualizzare le firme.
      </div>
    );
  }

  if (isParent) {
    return (
      <Card className="p-10 text-center border-red-200 bg-red-50 max-w-md mx-auto">
        <p className="text-red-700">Sezione riservata allo staff.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="signatures-audit-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <FileSignature size={22} className="text-sky-600" />
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              Firme &amp; Audit
            </h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Storico completo delle firme digitali OTP per i documenti legali
            della squadra.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="signatures-refresh-btn"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </Button>
          <Button
            onClick={exportCsv}
            disabled={!filtered.length}
            data-testid="signatures-export-csv-btn"
          >
            <Download size={16} className="mr-2" />
            Esporta CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="label-eyebrow">Totale firme</div>
          <div
            className="text-3xl font-bold mt-1"
            data-testid="signatures-stat-total"
          >
            {stats.total}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            <ShieldCheck size={12} className="inline mr-1" />
            Audit immutabile
          </div>
        </Card>
        <Card className="p-4">
          <div className="label-eyebrow">Ultimi 7 giorni</div>
          <div className="text-3xl font-bold mt-1 text-emerald-600">
            {stats.recent}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            <CalendarIcon size={12} className="inline mr-1" />
            firme recenti
          </div>
        </Card>
        <Card className="p-4 md:col-span-2">
          <div className="label-eyebrow">Per tipo documento</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(DOCUMENT_LABELS).map(([k, label]) => (
              <Badge
                key={k}
                variant="outline"
                className={`${DOCUMENT_COLORS[k]} font-medium`}
              >
                {label}: {stats.byType[k] || 0}
              </Badge>
            ))}
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="label-eyebrow mb-1.5 block">Cerca</label>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome, email, IP, riferimento..."
                className="pl-9"
                data-testid="signatures-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="label-eyebrow mb-1.5 block">Tipo documento</label>
            <Select value={docFilter} onValueChange={setDocFilter}>
              <SelectTrigger data-testid="signatures-doc-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i documenti</SelectItem>
                {Object.entries(DOCUMENT_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Dal</label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="signatures-from-date"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Al</label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="signatures-to-date"
            />
          </div>
          <div className="md:col-span-1">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setSearch("");
                setDocFilter("__all__");
                setFrom("");
                setTo("");
              }}
              data-testid="signatures-reset-filters"
            >
              Reset
            </Button>
          </div>
        </div>
        <div className="text-xs text-slate-500 mt-3">
          {filtered.length} di {signatures.length} firme
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="signatures-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Data &amp; ora</th>
                <th className="px-4 py-3 font-semibold">Documento</th>
                <th className="px-4 py-3 font-semibold">Firmatario</th>
                <th className="px-4 py-3 font-semibold">Riferimento</th>
                <th className="px-4 py-3 font-semibold">IP &amp; UA</th>
                <th className="px-4 py-3 font-semibold">Metodo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-slate-400"
                  >
                    Caricamento…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-slate-400"
                  >
                    {signatures.length === 0
                      ? "Nessuna firma registrata per questa squadra."
                      : "Nessuna firma corrisponde ai filtri."}
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((s) => (
                  <tr
                    key={s.id}
                    className="hover:bg-slate-50 transition"
                    data-testid={`signature-row-${s.id}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-slate-800">
                        {formatDateTime(s.signed_at)}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        {s.id?.slice(0, 12)}…
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={`${
                          DOCUMENT_COLORS[s.document_type] || ""
                        } font-medium`}
                      >
                        {DOCUMENT_LABELS[s.document_type] || s.document_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">
                        {s.signer_name || "—"}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Mail size={11} />
                        {s.signer_email}
                      </div>
                      {s.signer_role && (
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
                          {s.signer_role}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-mono text-slate-600 break-all"
                        title={s.document_ref}
                      >
                        {s.document_ref || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-mono text-slate-700 flex items-center gap-1">
                        <Globe size={11} className="text-slate-400" />
                        {s.ip_address || "—"}
                      </div>
                      <div
                        className="text-[10px] text-slate-400 max-w-[220px] truncate"
                        title={s.user_agent}
                      >
                        {s.user_agent || ""}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className="bg-emerald-50 text-emerald-700 border-emerald-200 font-medium"
                      >
                        <ShieldCheck size={11} className="mr-1" />
                        {s.method === "email_otp" ? "OTP email" : s.method}
                      </Badge>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-xs text-slate-400 text-center pb-6">
        Tutte le firme sono registrate con timestamp UTC, IP e user-agent al
        momento della verifica OTP. Il log è immutabile e può essere usato come
        evidenza legale (eIDAS — firma elettronica semplice).
      </div>
    </div>
  );
}
